import React, { useEffect, useState, useCallback } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Button, Grid, TextField } from '@material-ui/core';
import Typography from "@material-ui/core/Typography";
import { getTicketsDayReport } from '../../services/dashboard.api';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { i18n } from '../../translate/i18n';
import './button.css';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels
);

export const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            position: 'top',
            display: false,
        },
        title: {
            display: false,
        },
        datalabels: {
            display: true,
            anchor: 'end',
            align: "top",
            color: "#374151",
            font: {
                size: 12,
                weight: "600"
            },
            formatter: (value) => value || '',
        },
        tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            padding: 12,
            titleFont: {
                size: 14,
                weight: 'bold'
            },
            bodyFont: {
                size: 13
            },
            cornerRadius: 8,
            displayColors: false,
        }
    },
    scales: {
        y: {
            beginAtZero: true,
            grid: {
                color: 'rgba(0, 0, 0, 0.05)',
                drawBorder: false,
            },
            ticks: {
                font: {
                    size: 11
                },
                color: '#6B7280',
            }
        },
        x: {
            grid: {
                display: false,
            },
            ticks: {
                font: {
                    size: 11
                },
                color: '#6B7280',
                maxRotation: 45,
                minRotation: 45,
            }
        }
    },
};

export const ChartsDate = () => {

    // ✅ ESTABLECER FECHAS POR DEFECTO - Última semana
    const defaultInitialDate = new Date();
    defaultInitialDate.setDate(defaultInitialDate.getDate() - 7);
    
    const [initialDate, setInitialDate] = useState(defaultInitialDate);
    const [finalDate, setFinalDate] = useState(new Date());
    const [ticketsData, setTicketsData] = useState({ data: [], count: 0 });
    const [loading, setLoading] = useState(false);

    // companyId: lo pone el backend desde la sesión (doc 0431, C-04)

    const handleGetTicketsInformation = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await getTicketsDayReport({ initialDate: format(initialDate, 'yyyy-MM-dd'), finalDate: format(finalDate, 'yyyy-MM-dd') });
            setTicketsData(data);
        } catch (error) {
            // ✅ NO MOSTRAR ERROR SI ES 403 (Forbidden) - Probablemente falta token o expiró
            if (error.response && error.response.status === 403) {
                console.warn("⚠️ Error 403 al cargar datos por fecha - Token inválido o expirado");
                // No mostrar toast, el interceptor de API manejará el refresh token
            } else if (error.response && error.response.status !== 404) {
                console.error('Error cargando datos por fecha:', error);
                // Solo mostrar error si no es un error de autenticación
                if (error.response.status !== 401 && error.response.status !== 403) {
                    toast.error(i18n.t("dashboard.errors.errorGettingConversationInfo"));
                }
            } else {
                console.error('Error cargando datos por fecha:', error);
            }
            setTicketsData({ data: [], count: 0 });
        } finally {
            setLoading(false);
        }
    }, [initialDate, finalDate]);

    // ✅ CARGAR DATOS AUTOMÁTICAMENTE AL MOUNT
    useEffect(() => {
        handleGetTicketsInformation();
    }, [handleGetTicketsInformation]);

    // Generar colores con gradiente moderno
    const generateGradientColors = (count) => {
        const colors = [];
        const baseColors = [
            'rgba(59, 130, 246, 0.8)',   // Blue
            'rgba(16, 185, 129, 0.8)',   // Green
            'rgba(139, 92, 246, 0.8)',   // Purple
            'rgba(236, 72, 153, 0.8)',   // Pink
            'rgba(245, 158, 11, 0.8)',   // Amber
            'rgba(6, 182, 212, 0.8)',    // Cyan
            'rgba(99, 102, 241, 0.8)',   // Indigo
            'rgba(249, 115, 22, 0.8)',   // Orange
        ];
        
        for (let i = 0; i < count; i++) {
            colors.push(baseColors[i % baseColors.length]);
        }
        return colors;
    };

    const dataCharts = {
        labels: ticketsData && ticketsData?.data.length > 0 && ticketsData?.data.map((item) => 
            (item.hasOwnProperty('horario') ? `${item.horario}:00-${item.horario}:59` : item.data)
        ),
        datasets: [
            {
                label: 'Tickets',
                data: ticketsData?.data.length > 0 && ticketsData?.data.map((item) => item.total),
                backgroundColor: ticketsData?.data.length > 0 
                    ? generateGradientColors(ticketsData.data.length)
                    : ['rgba(59, 130, 246, 0.8)'],
                borderRadius: 8,
                borderSkipped: false,
                borderWidth: 0,
            },
        ],
    };

    return (
        <>
            <Typography 
                component="h2" 
                variant="h6" 
                style={{ 
                    fontWeight: 600, 
                    marginBottom: 20,
                    color: '#1F2937',
                    fontSize: '18px'
                }}
            >
                {i18n.t("dashboard.cards.totalAttendances")} 
                {ticketsData?.count && (
                    <span style={{ color: '#6B7280', fontWeight: 400, fontSize: '16px' }}>
                        {' '}({ticketsData.count})
                    </span>
                )}
            </Typography>

            <Grid container spacing={2} style={{ marginBottom: 24 }}>
                <Grid item xs={12} sm={6} md={4}>
                    <TextField
                        label={i18n.t("dashboard.filters.start")}
                        type="date"
                        value={initialDate instanceof Date && !isNaN(initialDate) ? format(initialDate, 'yyyy-MM-dd') : ''}
                        onChange={(e) => {
                            const date = new Date(e.target.value);
                            if (!isNaN(date.getTime())) {
                                setInitialDate(date);
                            }
                        }}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        fullWidth
                        variant="outlined"
                        size="small"
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                    <TextField
                        label={i18n.t("dashboard.filters.end")}
                        type="date"
                        value={finalDate instanceof Date && !isNaN(finalDate) ? format(finalDate, 'yyyy-MM-dd') : ''}
                        onChange={(e) => {
                            const date = new Date(e.target.value);
                            if (!isNaN(date.getTime())) {
                                setFinalDate(date);
                            }
                        }}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        fullWidth
                        variant="outlined"
                        size="small"
                    />
                </Grid>

                <Grid item xs={12} sm={12} md={4}>
                    <Button 
                        onClick={handleGetTicketsInformation} 
                        variant='contained' 
                        color="primary"
                        fullWidth
                        style={{ 
                            borderRadius: 8,
                            textTransform: 'none',
                            fontWeight: 600,
                            padding: '8px 16px'
                        }}
                    >
                        {i18n.t("dashboard.filters.filter")}
                    </Button>
                </Grid>
            </Grid>
            
            <div style={{ height: '350px', position: 'relative' }}>
                {loading ? (
                    <div style={{ 
                        textAlign: 'center', 
                        padding: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%'
                    }}>
                        <Typography style={{ color: '#6B7280' }}>Cargando datos...</Typography>
                    </div>
                ) : ticketsData?.data?.length > 0 ? (
                    <Bar options={options} data={dataCharts} />
                ) : (
                    <div style={{ 
                        textAlign: 'center', 
                        padding: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%'
                    }}>
                        <Typography style={{ color: '#6B7280' }}>No hay datos disponibles</Typography>
                    </div>
                )}
            </div>
        </>
    );
}